import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../student/Navbar';
import AdminSidebar from './AdminSidebar';
import Loader from '../components/Loader';
import { motion } from 'framer-motion';
import {
  FaChartLine,
  FaCalendarAlt,
  FaStar,
  FaUserAlt,
  FaUsers,
  FaUserGraduate,
  FaUserNurse,
  FaUserMd,
  FaClipboardCheck,
  FaChartPie,
  FaChartBar,
  FaChartArea,
  FaFileMedical
} from 'react-icons/fa';
import { Bar, Pie, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';

// Website color palette
const websiteColorPalette = {
  primary: '#0077B6',
  secondary: '#20B2AA',
  background: '#FFFFFF',
  text: '#333333',
  accent: '#28A745'
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

// Define a consistent color palette based on the website design system
const colorPalette = {
  primary: { main: `${websiteColorPalette.primary}CC`, border: websiteColorPalette.primary },
  secondary: { main: `${websiteColorPalette.secondary}CC`, border: websiteColorPalette.secondary },
  accent: { main: `${websiteColorPalette.accent}CC`, border: websiteColorPalette.accent },
  red: { main: 'rgba(239, 68, 68, 0.8)', border: 'rgba(239, 68, 68, 1)' },
  orange: { main: 'rgba(249, 115, 22, 0.8)', border: 'rgba(249, 115, 22, 1)' },
  amber: { main: 'rgba(245, 158, 11, 0.8)', border: 'rgba(245, 158, 11, 1)' },
};

const Analytics = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [appointments, setAppointments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [patients, setPatients] = useState([]);
  const [students, setStudents] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [treatmentSheets, setTreatmentSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user, token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !token) {
        setError('Please log in to view analytics.');
        setLoading(false);
        return;
      }

      if (!user.university) {
        setError('User profile incomplete. Missing university information.');
        setLoading(false);
        return;
      }

      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };

        // Fetch all data in parallel including reviews directly
        const initialRequests = [
          axios.get(`${process.env.REACT_APP_API_URL}/api/admin/appointments?university=${encodeURIComponent(user.university)}`, config),
          axios.get(`${process.env.REACT_APP_API_URL}/api/admin/patients?university=${encodeURIComponent(user.university)}`, config),
          axios.get(`${process.env.REACT_APP_API_URL}/api/admin/students?university=${encodeURIComponent(user.university)}`, config),
          axios.get(`${process.env.REACT_APP_API_URL}/api/admin/supervisors?university=${encodeURIComponent(user.university)}`, config),
          axios.get(`${process.env.REACT_APP_API_URL}/api/admin/assistants?university=${encodeURIComponent(user.university)}`, config),
          axios.get(`${process.env.REACT_APP_API_URL}/api/admin/treatment-sheets`, config),
          axios.get(`${process.env.REACT_APP_API_URL}/api/reviews`, config) // Direct reviews endpoint for admin
        ];

        // Execute all requests and handle errors individually
        const [appointmentsRes, patientsRes, studentsRes, supervisorsRes, assistantsRes, treatmentSheetsRes, reviewsRes] = await Promise.all(
          initialRequests.map(request =>
            request.catch(error => {
              console.error('API request failed:', error.message);
              return { data: [] }; // Return empty data on error
            })
          )
        );

        const students = studentsRes.data || [];
        
        // Process reviews - filter out signature storage and remove signature field
        let allReviews = [];
        if (reviewsRes.data && Array.isArray(reviewsRes.data)) {
          console.log('Reviews fetched:', reviewsRes.data.length);
          console.log('Sample review:', reviewsRes.data[0]);
          allReviews = reviewsRes.data
            .filter(review => !(review.patientId && review.patientId.nationalId === 'signature-storage'))
            .map(review => {
              const { signature, ...reviewWithoutSignature } = review;
              return reviewWithoutSignature;
            });
          console.log('Filtered reviews:', allReviews.length);
        }

        console.log('Appointments fetched:', appointmentsRes.data?.length || 0);
        console.log('Sample appointment:', appointmentsRes.data?.[0]);
        console.log('Treatment sheets fetched:', treatmentSheetsRes.data?.length || 0);
        console.log('Sample treatment sheet:', treatmentSheetsRes.data?.[0]);
        
        setAppointments(appointmentsRes.data || []);
        setReviews(allReviews);
        setPatients(patientsRes.data || []);
        setStudents(students);
        setSupervisors(supervisorsRes.data || []);
        setAssistants(assistantsRes.data || []);
        setTreatmentSheets(treatmentSheetsRes.data || []);

        if (
          appointmentsRes.data.length === 0 &&
          allReviews.length === 0 &&
          patientsRes.data.length === 0 &&
          treatmentSheetsRes.data.length === 0
        ) {
          // Remove the error message for no data found
          // setError('No data found for analytics.');
        }
      } catch (err) {
        const errorMessage =
          err.response?.status === 404
            ? 'Analytics endpoints not found.'
            : err.response?.status === 401
            ? 'Unauthorized. Please log in again.'
            : err.response?.data?.message || 'Failed to load analytics';
        setError(errorMessage);
        if (err.response?.status === 401) navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, token, navigate]);

  // Process appointments data
  const appointmentsAnalytics = {
    total: appointments.length,
    pending: appointments.filter((a) => a.status === 'pending').length,
    completed: appointments.filter((a) => a.status === 'completed').length,
    cancelled: appointments.filter((a) => a.status === 'cancelled').length,
    today: appointments.filter((a) => {
      const apptDate = new Date(a.date);
      const todayDate = new Date();
      return apptDate.toDateString() === todayDate.toDateString();
    }).length,
    thisWeek: appointments.filter((a) => {
      const apptDate = new Date(a.date);
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // End of week (Saturday)
      return apptDate >= startOfWeek && apptDate <= endOfWeek;
    }).length,
    byMonth: getAppointmentsByMonth(appointments),
    byDay: getAppointmentsByDay(appointments),
    byType: getAppointmentsByType(appointments)
  };

  // Filter out signature storage reviews
  const filteredReviews = reviews.filter(review =>
    !(review.patientId && review.patientId.nationalId === 'signature-storage')
  );

  // Process reviews data
  const reviewsAnalytics = {
    total: filteredReviews.length,
    pending: filteredReviews.filter(r => r.status === 'pending').length,
    accepted: filteredReviews.filter(r => r.status === 'accepted').length,
    declined: filteredReviews.filter(r => r.status === 'declined' || r.status === 'denied').length,
    avgProcedureQuality: calculateAverageRating(filteredReviews, 'procedureQuality'),
    avgPatientInteraction: calculateAverageRating(filteredReviews, 'patientInteraction'),
    byProcedureType: getReviewsByProcedureType(filteredReviews),
    byMonth: getReviewsByMonth(filteredReviews),
    byStudent: getReviewsByStudent(filteredReviews)
  };


  // Process patients data
  const patientsAnalytics = {
    total: patients.length,
    byAge: getPatientsAgeDistribution(patients),
    byGender: getPatientsGenderDistribution(patients),
    byChronicDisease: getPatientsChronicDiseaseDistribution(patients)
  };

  // People analytics
  const peopleAnalytics = {
    students: students.length,
    supervisors: supervisors.length,
    assistants: assistants.length,
    patients: patients.length
  };

  // Process treatment sheets data
  const proceduresAnalytics = {
    total: treatmentSheets.length,
    byType: getProceduresByType(treatmentSheets),
    byMonth: getProceduresByMonth(treatmentSheets),
    byStudent: getProceduresByStudent(treatmentSheets),
    byPatient: getProceduresByPatient(treatmentSheets),
    recentSheets: treatmentSheets
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 10)
  };

  // Chart data for overview
  const overviewChartData = {
    labels: ['Patients', 'Students', 'Supervisors', 'Assistants', 'Appointments', 'Reviews', 'Procedures'],
    datasets: [
      {
        label: 'Counts',
        data: [
          peopleAnalytics.patients,
          peopleAnalytics.students,
          peopleAnalytics.supervisors,
          peopleAnalytics.assistants,
          appointmentsAnalytics.total,
          reviewsAnalytics.total,
          proceduresAnalytics.total,
        ],
        backgroundColor: [
          colorPalette.primary.main,
          colorPalette.secondary.main,
          colorPalette.accent.main,
          colorPalette.primary.main,
          colorPalette.secondary.main,
          colorPalette.accent.main,
          colorPalette.orange.main,
        ],
        borderColor: [
          colorPalette.primary.border,
          colorPalette.secondary.border,
          colorPalette.accent.border,
          colorPalette.primary.border,
          colorPalette.secondary.border,
          colorPalette.accent.border,
          colorPalette.orange.border,
        ],
        borderWidth: 1,
        borderRadius: 6,
        hoverOffset: 4,
      },
    ],
  };

  // Student Performance Chart Data
  const studentLabels = Object.keys(reviewsAnalytics.byStudent).length > 0 
    ? Object.keys(reviewsAnalytics.byStudent).slice(0, 8) 
    : ['No Data'];
  const studentPerformanceChartData = {
    labels: studentLabels,
    datasets: [
      {
        label: 'Total Reviews',
        data: Object.keys(reviewsAnalytics.byStudent).length > 0 
          ? Object.values(reviewsAnalytics.byStudent).slice(0, 8).map(student => student.total)
          : [0],
        backgroundColor: colorPalette.primary.main,
        borderColor: colorPalette.primary.border,
        borderWidth: 1,
        borderRadius: 6,
      },
      {
        label: 'Accepted Reviews',
        data: Object.keys(reviewsAnalytics.byStudent).length > 0 
          ? Object.values(reviewsAnalytics.byStudent).slice(0, 8).map(student => student.accepted)
          : [0],
        backgroundColor: colorPalette.accent.main,
        borderColor: colorPalette.accent.border,
        borderWidth: 1,
        borderRadius: 6,
      }
    ],
  };

  // Clinic Efficiency Chart Data
  const clinicEfficiencyLabels = appointmentsAnalytics.byMonth && appointmentsAnalytics.byMonth.length > 0 
    ? appointmentsAnalytics.byMonth.map(item => item.month) 
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const clinicEfficiencyChartData = {
    labels: clinicEfficiencyLabels,
    datasets: [
      {
        label: 'Appointments',
        data: appointmentsAnalytics.byMonth && appointmentsAnalytics.byMonth.length > 0 
          ? appointmentsAnalytics.byMonth.map(item => item.count)
          : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        backgroundColor: colorPalette.primary.main,
        borderColor: colorPalette.primary.border,
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Procedures',
        data: proceduresAnalytics.byMonth && proceduresAnalytics.byMonth.length > 0 
          ? proceduresAnalytics.byMonth.map(item => item.count)
          : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        backgroundColor: colorPalette.secondary.main,
        borderColor: colorPalette.secondary.border,
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      }
    ],
  };

  // Patient Growth Trends Chart Data
  const patientGrowthTrendsData = {
    labels: getPatientTrends().months,
    datasets: [{
      label: 'New Patients',
      data: getPatientTrends().patientCounts,
      backgroundColor: 'rgba(0, 119, 182, 0.1)',
      borderColor: '#0077B6',
      borderWidth: 3,
      tension: 0.4,
      fill: true,
      pointBackgroundColor: '#0077B6',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8
    }]
  };

  // Review Quality Trends Chart Data
  const reviewQualityLabels = reviewsAnalytics.byMonth && reviewsAnalytics.byMonth.length > 0 
    ? reviewsAnalytics.byMonth.map(item => item.month) 
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const reviewQualityTrendsData = {
    labels: reviewQualityLabels,
    datasets: [
      {
        label: 'Average Procedure Quality',
        data: reviewsAnalytics.byMonth && reviewsAnalytics.byMonth.length > 0 
          ? reviewsAnalytics.byMonth.map(item => item.avgProcedureQuality || 0)
          : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        backgroundColor: colorPalette.accent.main,
        borderColor: colorPalette.accent.border,
        borderWidth: 2,
        tension: 0.4,
        fill: false,
      },
      {
        label: 'Average Patient Interaction',
        data: reviewsAnalytics.byMonth && reviewsAnalytics.byMonth.length > 0 
          ? reviewsAnalytics.byMonth.map(item => item.avgPatientInteraction || 0)
          : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        backgroundColor: colorPalette.secondary.main,
        borderColor: colorPalette.secondary.border,
        borderWidth: 2,
        tension: 0.4,
        fill: false,
      }
    ],
  };

  // Helper function to get patient trends
  function getPatientTrends() {
    const months = [];
    const patientCounts = [];
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthName = date.toLocaleString('default', { month: 'short' });
      months.push(monthName);
      
      // Count patients created in this month
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const count = patients.filter(patient => {
        const createdDate = patient.registrationDate || patient.createdAt;
        if (!createdDate) return false;
        
        try {
          const patientDate = new Date(createdDate);
          return patientDate >= monthStart && patientDate <= monthEnd;
        } catch (e) {
          return false;
        }
      }).length;
      
      patientCounts.push(count);
    }
    
    return { months, patientCounts };
  }

  // Appointments status chart data
  const hasAppointmentsData = appointmentsAnalytics.pending > 0 || appointmentsAnalytics.completed > 0 || appointmentsAnalytics.cancelled > 0;
  const appointmentsStatusChartData = {
    labels: ['Pending', 'Completed', 'Cancelled'],
    datasets: [
      {
        data: hasAppointmentsData ? [
          appointmentsAnalytics.pending,
          appointmentsAnalytics.completed,
          appointmentsAnalytics.cancelled,
        ] : [1, 1, 1],
        backgroundColor: hasAppointmentsData ? [
          colorPalette.amber.main,
          colorPalette.accent.main,
          colorPalette.red.main,
        ] : ['rgba(200, 200, 200, 0.5)', 'rgba(200, 200, 200, 0.5)', 'rgba(200, 200, 200, 0.5)'],
        borderColor: [
          colorPalette.amber.border,
          colorPalette.accent.border,
          colorPalette.red.border,
        ],
        borderWidth: 1,
        hoverOffset: 8,
      },
    ],
  };

  // Appointments by month chart data
  const apptByMonthLabels = appointmentsAnalytics.byMonth && appointmentsAnalytics.byMonth.length > 0 
    ? appointmentsAnalytics.byMonth.map(item => item.month) 
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const appointmentsByMonthChartData = {
    labels: apptByMonthLabels,
    datasets: [
      {
        label: 'Appointments',
        data: appointmentsAnalytics.byMonth && appointmentsAnalytics.byMonth.length > 0 
          ? appointmentsAnalytics.byMonth.map(item => item.count)
          : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        backgroundColor: colorPalette.primary.main,
        borderColor: colorPalette.primary.border,
        borderWidth: 1,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Appointments by day chart data
  const apptByDayLabels = appointmentsAnalytics.byDay && appointmentsAnalytics.byDay.length > 0 
    ? appointmentsAnalytics.byDay.map(item => item.day) 
    : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const appointmentsByDayChartData = {
    labels: apptByDayLabels,
    datasets: [
      {
        label: 'Appointments',
        data: appointmentsAnalytics.byDay && appointmentsAnalytics.byDay.length > 0 
          ? appointmentsAnalytics.byDay.map(item => item.count)
          : [0, 0, 0, 0, 0, 0, 0],
        backgroundColor: colorPalette.secondary.main,
        borderColor: colorPalette.secondary.border,
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  // Appointments by type chart data
  const appointmentTypeLabels = Object.keys(appointmentsAnalytics.byType).length > 0 
    ? Object.keys(appointmentsAnalytics.byType) 
    : ['No Data'];
  const appointmentTypeData = Object.values(appointmentsAnalytics.byType).length > 0 
    ? Object.values(appointmentsAnalytics.byType) 
    : [1];
  const appointmentsByTypeChartData = {
    labels: appointmentTypeLabels,
    datasets: [
      {
        data: appointmentTypeData,
        backgroundColor: appointmentTypeLabels.map((_, index) => {
          if (Object.keys(appointmentsAnalytics.byType).length === 0) return 'rgba(200, 200, 200, 0.5)';
          const colorKeys = Object.keys(colorPalette);
          return colorPalette[colorKeys[index % colorKeys.length]].main;
        }),
        borderColor: appointmentTypeLabels.map((_, index) => {
          if (Object.keys(appointmentsAnalytics.byType).length === 0) return 'rgba(200, 200, 200, 1)';
          const colorKeys = Object.keys(colorPalette);
          return colorPalette[colorKeys[index % colorKeys.length]].border;
        }),
        borderWidth: 1,
        hoverOffset: 8,
      },
    ],
  };

  // Reviews status chart data
  const hasReviewsData = reviewsAnalytics.pending > 0 || reviewsAnalytics.accepted > 0 || reviewsAnalytics.declined > 0;
  const reviewsStatusChartData = {
    labels: ['Pending', 'Accepted', 'Declined'],
    datasets: [
      {
        data: hasReviewsData ? [
          reviewsAnalytics.pending,
          reviewsAnalytics.accepted,
          reviewsAnalytics.declined,
        ] : [1, 1, 1],
        backgroundColor: hasReviewsData ? [
          colorPalette.amber.main,
          colorPalette.accent.main,
          colorPalette.red.main,
        ] : ['rgba(200, 200, 200, 0.5)', 'rgba(200, 200, 200, 0.5)', 'rgba(200, 200, 200, 0.5)'],
        borderColor: [
          colorPalette.amber.border,
          colorPalette.accent.border,
          colorPalette.red.border,
        ],
        borderWidth: 1,
        hoverOffset: 8,
      },
    ],
  };

  // Reviews by procedure type chart data
  const procedureTypeLabels = Object.keys(reviewsAnalytics.byProcedureType).length > 0 
    ? Object.keys(reviewsAnalytics.byProcedureType) 
    : ['No Data'];
  const procedureTypeData = Object.values(reviewsAnalytics.byProcedureType).length > 0 
    ? Object.values(reviewsAnalytics.byProcedureType) 
    : [1];
  const reviewsByProcedureTypeChartData = {
    labels: procedureTypeLabels,
    datasets: [
      {
        data: procedureTypeData,
        backgroundColor: procedureTypeLabels.map((_, index) => {
          if (Object.keys(reviewsAnalytics.byProcedureType).length === 0) return 'rgba(200, 200, 200, 0.5)';
          const colorKeys = Object.keys(colorPalette);
          return colorPalette[colorKeys[index % colorKeys.length]].main;
        }),
        borderColor: procedureTypeLabels.map((_, index) => {
          if (Object.keys(reviewsAnalytics.byProcedureType).length === 0) return 'rgba(200, 200, 200, 1)';
          const colorKeys = Object.keys(colorPalette);
          return colorPalette[colorKeys[index % colorKeys.length]].border;
        }),
        borderWidth: 1,
        hoverOffset: 8,
      },
    ],
  };

  // Reviews by month chart data - use Bar chart format
  const reviewsByMonthLabels = reviewsAnalytics.byMonth && reviewsAnalytics.byMonth.length > 0 
    ? reviewsAnalytics.byMonth.map(item => item.month) 
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const reviewsByMonthData = reviewsAnalytics.byMonth && reviewsAnalytics.byMonth.length > 0 
    ? reviewsAnalytics.byMonth.map(item => item.count) 
    : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  const reviewsByMonthChartData = {
    labels: reviewsByMonthLabels,
    datasets: [
      {
        label: 'Reviews',
        data: reviewsByMonthData,
        backgroundColor: reviewsByMonthLabels.map((_, index) => {
          const colors = [
            colorPalette.primary.main,
            colorPalette.secondary.main,
            colorPalette.accent.main,
            colorPalette.orange.main,
            colorPalette.amber.main,
            colorPalette.red.main,
          ];
          return colors[index % colors.length];
        }),
        borderColor: reviewsByMonthLabels.map((_, index) => {
          const colors = [
            colorPalette.primary.border,
            colorPalette.secondary.border,
            colorPalette.accent.border,
            colorPalette.orange.border,
            colorPalette.amber.border,
            colorPalette.red.border,
          ];
          return colors[index % colors.length];
        }),
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  // Patients by age chart data
  const patientsByAgeChartData = {
    labels: Object.keys(patientsAnalytics.byAge),
    datasets: [
      {
        label: 'Patients',
        data: Object.values(patientsAnalytics.byAge),
        backgroundColor: colorPalette.accent.main,
        borderColor: colorPalette.accent.border,
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  // Patients by gender chart data
  const hasGenderData = Object.values(patientsAnalytics.byGender).some(v => v > 0);
  const patientsByGenderChartData = {
    labels: Object.keys(patientsAnalytics.byGender),
    datasets: [
      {
        data: hasGenderData ? Object.values(patientsAnalytics.byGender) : [1, 1, 1],
        backgroundColor: hasGenderData ? [
          colorPalette.primary.main,
          colorPalette.secondary.main,
          colorPalette.accent.main,
        ] : ['rgba(200, 200, 200, 0.5)', 'rgba(200, 200, 200, 0.5)', 'rgba(200, 200, 200, 0.5)'],
        borderColor: [
          colorPalette.primary.border,
          colorPalette.secondary.border,
          colorPalette.accent.border,
        ],
        borderWidth: 1,
        hoverOffset: 8,
      },
    ],
  };

  // Patients by chronic disease chart data
  const chronicDiseaseLabels = Object.keys(patientsAnalytics.byChronicDisease).length > 0 
    ? Object.keys(patientsAnalytics.byChronicDisease).slice(0, 5) 
    : ['No Data'];
  const patientsByChronicDiseaseChartData = {
    labels: chronicDiseaseLabels,
    datasets: [
      {
        label: 'Patients',
        data: Object.keys(patientsAnalytics.byChronicDisease).length > 0 
          ? Object.values(patientsAnalytics.byChronicDisease).slice(0, 5)
          : [0],
        backgroundColor: chronicDiseaseLabels.map((_, index) => {
          if (Object.keys(patientsAnalytics.byChronicDisease).length === 0) return 'rgba(200, 200, 200, 0.5)';
          const colorKeys = Object.keys(colorPalette);
          return colorPalette[colorKeys[index % colorKeys.length]].main;
        }),
        borderColor: chronicDiseaseLabels.map((_, index) => {
          if (Object.keys(patientsAnalytics.byChronicDisease).length === 0) return 'rgba(200, 200, 200, 1)';
          const colorKeys = Object.keys(colorPalette);
          return colorPalette[colorKeys[index % colorKeys.length]].border;
        }),
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  // Procedures by type chart data
  const proceduresTypeLabels = Object.keys(proceduresAnalytics.byType).length > 0 
    ? Object.keys(proceduresAnalytics.byType) 
    : ['No Data'];
  const proceduresTypeData = Object.values(proceduresAnalytics.byType).length > 0 
    ? Object.values(proceduresAnalytics.byType) 
    : [1];
  const proceduresByTypeChartData = {
    labels: proceduresTypeLabels,
    datasets: [
      {
        data: proceduresTypeData,
        backgroundColor: proceduresTypeLabels.map((_, index) => {
          if (Object.keys(proceduresAnalytics.byType).length === 0) return 'rgba(200, 200, 200, 0.5)';
          const colorKeys = Object.keys(colorPalette);
          return colorPalette[colorKeys[index % colorKeys.length]].main;
        }),
        borderColor: proceduresTypeLabels.map((_, index) => {
          if (Object.keys(proceduresAnalytics.byType).length === 0) return 'rgba(200, 200, 200, 1)';
          const colorKeys = Object.keys(colorPalette);
          return colorPalette[colorKeys[index % colorKeys.length]].border;
        }),
        borderWidth: 1,
        hoverOffset: 8,
      },
    ],
  };

  // Procedures by month chart data
  const procByMonthLabels = proceduresAnalytics.byMonth && proceduresAnalytics.byMonth.length > 0 
    ? proceduresAnalytics.byMonth.map(item => item.month) 
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const proceduresByMonthChartData = {
    labels: procByMonthLabels,
    datasets: [
      {
        label: 'Procedures',
        data: proceduresAnalytics.byMonth && proceduresAnalytics.byMonth.length > 0 
          ? proceduresAnalytics.byMonth.map(item => item.count)
          : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        backgroundColor: procByMonthLabels.map((_, index) => {
          const colors = [
            colorPalette.primary.main,
            colorPalette.secondary.main,
            colorPalette.accent.main,
            colorPalette.orange.main,
            colorPalette.amber.main,
            colorPalette.red.main,
          ];
          return colors[index % colors.length];
        }),
        borderColor: procByMonthLabels.map((_, index) => {
          const colors = [
            colorPalette.primary.border,
            colorPalette.secondary.border,
            colorPalette.accent.border,
            colorPalette.orange.border,
            colorPalette.amber.border,
            colorPalette.red.border,
          ];
          return colors[index % colors.length];
        }),
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  // Procedures by student chart data
  const procByStudentLabels = Object.keys(proceduresAnalytics.byStudent).length > 0 
    ? Object.keys(proceduresAnalytics.byStudent).slice(0, 10) 
    : ['No Data'];
  const proceduresByStudentChartData = {
    labels: procByStudentLabels,
    datasets: [
      {
        label: 'Procedures',
        data: Object.keys(proceduresAnalytics.byStudent).length > 0 
          ? Object.values(proceduresAnalytics.byStudent).slice(0, 10)
          : [0],
        backgroundColor: colorPalette.primary.main,
        borderColor: colorPalette.primary.border,
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  // Procedures by patient chart data
  const procByPatientLabels = Object.keys(proceduresAnalytics.byPatient).length > 0 
    ? Object.keys(proceduresAnalytics.byPatient).slice(0, 8) 
    : ['No Data'];
  const proceduresByPatientChartData = {
    labels: procByPatientLabels,
    datasets: [
      {
        label: 'Procedures',
        data: Object.keys(proceduresAnalytics.byPatient).length > 0 
          ? Object.values(proceduresAnalytics.byPatient).slice(0, 8)
          : [0],
        backgroundColor: colorPalette.secondary.main,
        borderColor: colorPalette.secondary.border,
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  // Chart options
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            family: "'Inter', sans-serif",
            size: 12
          },
          padding: 20,
          usePointStyle: true,
          boxWidth: 8
        }
      },
      title: {
        display: false,
        font: {
          family: "'Inter', sans-serif",
          size: 16,
          weight: 'bold'
        },
        color: '#1e3a8a',
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#1e3a8a',
        bodyColor: '#4b5563',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        bodyFont: {
          family: "'Inter', sans-serif"
        },
        titleFont: {
          family: "'Inter', sans-serif",
          weight: 'bold'
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(226, 232, 240, 0.6)'
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif",
            size: 11
          },
          color: '#64748b'
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif",
            size: 11
          },
          color: '#64748b'
        }
      }
    }
  };

  // Pie chart options
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          font: {
            family: "'Inter', sans-serif",
            size: 12
          },
          padding: 20,
          usePointStyle: true,
          boxWidth: 8
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#1e3a8a',
        bodyColor: '#4b5563',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        bodyFont: {
          family: "'Inter', sans-serif"
        },
        titleFont: {
          family: "'Inter', sans-serif",
          weight: 'bold'
        }
      }
    },
    cutout: '60%',
    radius: '90%'
  };

  // Line chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            family: "'Inter', sans-serif",
            size: 12
          },
          padding: 20,
          usePointStyle: true,
          boxWidth: 8
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#1e3a8a',
        bodyColor: '#4b5563',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        bodyFont: {
          family: "'Inter', sans-serif"
        },
        titleFont: {
          family: "'Inter', sans-serif",
          weight: 'bold'
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(226, 232, 240, 0.6)'
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif",
            size: 11
          },
          color: '#64748b'
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif",
            size: 11
          },
          color: '#64748b'
        }
      }
    }
  };

  // Helper function to get appointments by month
  function getAppointmentsByMonth(appointments) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const appointmentsByMonth = {};

    // Initialize all months with 0
    months.forEach(month => {
      appointmentsByMonth[month] = 0;
    });

    // Count appointments by month
    appointments.forEach(appointment => {
      const date = parseFirestoreDate(appointment.date) || parseFirestoreDate(appointment.createdAt);
      if (!date) return;
      const month = months[date.getMonth()];
      appointmentsByMonth[month]++;
    });

    // Convert to array for chart
    return months.map(month => ({
      month,
      count: appointmentsByMonth[month]
    }));
  }

  // Helper function to get appointments by day of week
  function getAppointmentsByDay(appointments) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const appointmentsByDay = {};

    // Initialize all days with 0
    days.forEach(day => {
      appointmentsByDay[day] = 0;
    });

    // Count appointments by day
    appointments.forEach(appointment => {
      const date = parseFirestoreDate(appointment.date) || parseFirestoreDate(appointment.createdAt);
      if (!date) return;
      const day = days[date.getDay()];
      appointmentsByDay[day]++;
    });

    // Convert to array for chart
    return days.map(day => ({
      day,
      count: appointmentsByDay[day]
    }));
  }

  // Helper function to get appointments by type
  function getAppointmentsByType(appointments) {
    const appointmentsByType = {};

    // Count appointments by type
    appointments.forEach(appointment => {
      const type = appointment.type || 'Other';
      appointmentsByType[type] = (appointmentsByType[type] || 0) + 1;
    });

    return appointmentsByType;
  }

  // Helper function to get reviews by procedure type
  function getReviewsByProcedureType(reviews) {
    const reviewsByProcedureType = {};

    // Count reviews by procedure type
    reviews.forEach(review => {
      // Skip signature storage reviews
      if (review.patientId && review.patientId.nationalId === 'signature-storage') return;

      let type = 'Other';

      // Handle different possible formats of procedureType
      if (review.procedureType) {
        type = review.procedureType;
      } else if (review.procedure && review.procedure.type) {
        type = review.procedure.type;
      }

      reviewsByProcedureType[type] = (reviewsByProcedureType[type] || 0) + 1;
    });

    // If we have too many categories, limit to top 5 plus "Other"
    const entries = Object.entries(reviewsByProcedureType);
    if (entries.length > 6) {
      const sortedEntries = entries.sort((a, b) => b[1] - a[1]);
      const top5 = sortedEntries.slice(0, 5);
      const others = sortedEntries.slice(5).reduce((sum, entry) => sum + entry[1], 0);

      const result = Object.fromEntries(top5);
      result['Other'] = others;
      return result;
    }

    return reviewsByProcedureType;
  }

  // Helper function to parse Firestore timestamps
  function parseFirestoreDate(dateValue) {
    if (!dateValue) return null;
    
    // Handle Firestore timestamp with _seconds
    if (dateValue && typeof dateValue === 'object' && dateValue._seconds) {
      return new Date(dateValue._seconds * 1000);
    }
    
    // Handle Firestore timestamp with seconds (different format)
    if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
      return new Date(dateValue.seconds * 1000);
    }
    
    // Handle regular date strings or Date objects
    if (typeof dateValue === 'string' || dateValue instanceof Date) {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    }
    
    return null;
  }

  // Helper function to get reviews by month
  function getReviewsByMonth(reviews) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const reviewsByMonth = {};

    // Initialize all months with 0
    months.forEach(month => {
      reviewsByMonth[month] = {
        count: 0,
        avgProcedureQuality: 0,
        avgPatientInteraction: 0,
        totalQuality: 0,
        totalInteraction: 0,
        qualityCount: 0,
        interactionCount: 0
      };
    });

    // Count reviews by month and calculate averages
    reviews.forEach(review => {
      try {
        // Skip signature storage reviews
        if (review.patientId && review.patientId.nationalId === 'signature-storage') return;

        // Try different date fields that might exist in the review object
        let date = null;
        if (review.submittedDate) date = parseFirestoreDate(review.submittedDate);
        if (!date && review.createdAt) date = parseFirestoreDate(review.createdAt);
        if (!date && review.date) date = parseFirestoreDate(review.date);
        if (!date && review.reviewedDate) date = parseFirestoreDate(review.reviewedDate);

        if (!date) return; // Skip if no valid date found

        const month = months[date.getMonth()];
        reviewsByMonth[month].count++;

        // Calculate quality metrics
        let procedureQuality = 0;
        let patientInteraction = 0;

        if (review.procedureQuality && typeof review.procedureQuality === 'number') {
          procedureQuality = review.procedureQuality;
        } else if (review.ratings && review.ratings.procedureQuality && typeof review.ratings.procedureQuality === 'number') {
          procedureQuality = review.ratings.procedureQuality;
        }

        if (review.patientInteraction && typeof review.patientInteraction === 'number') {
          patientInteraction = review.patientInteraction;
        } else if (review.ratings && review.ratings.patientInteraction && typeof review.ratings.patientInteraction === 'number') {
          patientInteraction = review.ratings.patientInteraction;
        }

        if (procedureQuality > 0) {
          reviewsByMonth[month].totalQuality += procedureQuality;
          reviewsByMonth[month].qualityCount++;
        }

        if (patientInteraction > 0) {
          reviewsByMonth[month].totalInteraction += patientInteraction;
          reviewsByMonth[month].interactionCount++;
        }
      } catch (error) {
        // Skip invalid dates
      }
    });

    // Calculate averages and convert to array for chart
    return months.map(month => ({
      month,
      count: reviewsByMonth[month].count,
      avgProcedureQuality: reviewsByMonth[month].qualityCount > 0 ? 
        (reviewsByMonth[month].totalQuality / reviewsByMonth[month].qualityCount).toFixed(1) : 0,
      avgPatientInteraction: reviewsByMonth[month].interactionCount > 0 ? 
        (reviewsByMonth[month].totalInteraction / reviewsByMonth[month].interactionCount).toFixed(1) : 0
    }));
  }

  // Helper function to get reviews by student
  function getReviewsByStudent(reviews) {
    const reviewsByStudent = {};

    // Count reviews by student
    reviews.forEach(review => {
      // Skip signature storage reviews
      if (review.patientId && review.patientId.nationalId === 'signature-storage') return;

      // Get student name from different possible formats
      let student = 'Unknown';
      if (review.studentName) {
        student = review.studentName;
      } else if (review.student && review.student.name) {
        student = review.student.name;
      } else if (review.studentId) {
        // Try to find student name from students array
        const foundStudent = students.find(s => s._id === review.studentId || s.studentId === review.studentId);
        if (foundStudent) {
          student = foundStudent.name;
        }
      }

      if (!reviewsByStudent[student]) {
        reviewsByStudent[student] = {
          total: 0,
          accepted: 0,
          declined: 0,
          pending: 0
        };
      }

      reviewsByStudent[student].total++;
      if (review.status === 'accepted') reviewsByStudent[student].accepted++;
      else if (review.status === 'declined' || review.status === 'denied') reviewsByStudent[student].declined++;
      else reviewsByStudent[student].pending++;
    });

    // Sort by total reviews and limit to top 10 students
    const sortedStudents = Object.entries(reviewsByStudent)
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 10);

    return Object.fromEntries(sortedStudents);
  }

  // Helper function to calculate average rating
  function calculateAverageRating(reviews, field) {
    // Filter reviews that have a valid rating for the specified field
    const validReviews = reviews.filter(review => {
      // Skip signature storage reviews
      if (review.patientId && review.patientId.nationalId === 'signature-storage') return false;

      // Check direct field
      if (review[field] && typeof review[field] === 'number' && review[field] > 0) {
        return true;
      }

      // Check nested fields (e.g., ratings.procedureQuality)
      if (review.ratings && review.ratings[field] && typeof review.ratings[field] === 'number' && review.ratings[field] > 0) {
        return true;
      }

      return false;
    });

    if (validReviews.length === 0) return 0;

    // Calculate sum considering both direct and nested fields
    const sum = validReviews.reduce((total, review) => {
      if (review[field] && typeof review[field] === 'number') {
        return total + review[field];
      } else if (review.ratings && review.ratings[field] && typeof review.ratings[field] === 'number') {
        return total + review.ratings[field];
      }
      return total;
    }, 0);

    return (sum / validReviews.length).toFixed(1);
  }

  // Helper function to get patients age distribution
  function getPatientsAgeDistribution(patients) {
    const ageGroups = {
      '0-18': 0,
      '19-30': 0,
      '31-45': 0,
      '46-60': 0,
      '61+': 0
    };

    patients.forEach(patient => {
      const age = patient.age || 0;
      if (age <= 18) ageGroups['0-18']++;
      else if (age <= 30) ageGroups['19-30']++;
      else if (age <= 45) ageGroups['31-45']++;
      else if (age <= 60) ageGroups['46-60']++;
      else ageGroups['61+']++;
    });

    return ageGroups;
  }

  // Helper function to get patients gender distribution
  function getPatientsGenderDistribution(patients) {
    const genderDistribution = {
      'Male': 0,
      'Female': 0,
      'Other': 0
    };

    patients.forEach(patient => {
      const gender = patient.gender || 'Other';
      genderDistribution[gender] = (genderDistribution[gender] || 0) + 1;
    });

    return genderDistribution;
  }

  // Helper function to get patients chronic disease distribution
  function getPatientsChronicDiseaseDistribution(patients) {
    const diseaseDistribution = {};

    patients.forEach(patient => {
      if (patient.medicalInfo && patient.medicalInfo.chronicDiseases) {
        patient.medicalInfo.chronicDiseases.forEach(disease => {
          diseaseDistribution[disease] = (diseaseDistribution[disease] || 0) + 1;
        });
      }
    });

    // Sort by count and return
    return Object.fromEntries(
      Object.entries(diseaseDistribution).sort((a, b) => b[1] - a[1])
    );
  }

  // Helper function to get procedures by type
  function getProceduresByType(sheets) {
    const proceduresByType = {};

    sheets.forEach(sheet => {
      // Try different field names for procedure type
      const type = sheet.type || sheet.procedureType || sheet.treatmentType || 'Other';
      proceduresByType[type] = (proceduresByType[type] || 0) + 1;
    });

    // If empty, return with "No Data"
    if (Object.keys(proceduresByType).length === 0) {
      return { 'No Data': 0 };
    }

    return proceduresByType;
  }

  // Helper function to get procedures by month
  function getProceduresByMonth(sheets) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const proceduresByMonth = {};

    // Initialize all months with 0
    months.forEach(month => {
      proceduresByMonth[month] = 0;
    });

    // Count procedures by month
    sheets.forEach(sheet => {
      try {
        const date = parseFirestoreDate(sheet.createdAt) || parseFirestoreDate(sheet.date);
        if (!date) return; // Skip if date is invalid

        const month = months[date.getMonth()];
        proceduresByMonth[month]++;
      } catch (error) {
        // Skip invalid dates
      }
    });

    // Convert to array for chart
    return months.map(month => ({
      month,
      count: proceduresByMonth[month]
    }));
  }

  // Helper function to get procedures by student
  function getProceduresByStudent(sheets) {
    const proceduresByStudent = {};

    sheets.forEach(sheet => {
      const student = sheet.studentName || 'Unknown Student';
      proceduresByStudent[student] = (proceduresByStudent[student] || 0) + 1;
    });

    // Sort by count and return
    return Object.fromEntries(
      Object.entries(proceduresByStudent).sort((a, b) => b[1] - a[1])
    );
  }

  // Helper function to get procedures by patient
  function getProceduresByPatient(sheets) {
    const proceduresByPatient = {};

    sheets.forEach(sheet => {
      const patient = sheet.patientName || 'Unknown Patient';
      proceduresByPatient[patient] = (proceduresByPatient[patient] || 0) + 1;
    });

    // Sort by count and return top 10
    return Object.fromEntries(
      Object.entries(proceduresByPatient).sort((a, b) => b[1] - a[1]).slice(0, 10)
    );
  }

  const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  if (loading) return <Loader />;

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm"
              >
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              </motion.div>
            )}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
              <div className="mb-6 sm:mb-8">
                <h1 className={`text-2xl sm:text-3xl md:text-4xl font-bold text-[${websiteColorPalette.primary}] mb-1`}>Analytics</h1>
                <p className={`text-[${websiteColorPalette.text}]`}>Comprehensive analytics for your university</p>
              </div>

              {/* Navigation Tabs */}
              <motion.div
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 overflow-hidden mb-6"
              >
                <div className="border-b border-gray-200">
                  <nav className="flex flex-wrap -mb-px">
                    <button
                      className={`py-3 sm:py-4 px-3 sm:px-6 font-medium text-xs sm:text-sm border-b-2 ${
                        activeTab === 'overview'
                          ? `border-[${websiteColorPalette.primary}] text-[${websiteColorPalette.primary}]`
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => setActiveTab('overview')}
                    >
                      Overview
                    </button>
                    <button
                      className={`py-3 sm:py-4 px-3 sm:px-6 font-medium text-xs sm:text-sm border-b-2 ${
                        activeTab === 'appointments'
                          ? `border-[${websiteColorPalette.primary}] text-[${websiteColorPalette.primary}]`
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => setActiveTab('appointments')}
                    >
                      Appointments
                    </button>
                    <button
                      className={`py-3 sm:py-4 px-3 sm:px-6 font-medium text-xs sm:text-sm border-b-2 ${
                        activeTab === 'reviews'
                          ? `border-[${websiteColorPalette.primary}] text-[${websiteColorPalette.primary}]`
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => setActiveTab('reviews')}
                    >
                      Reviews
                    </button>
                    <button
                      className={`py-3 sm:py-4 px-3 sm:px-6 font-medium text-xs sm:text-sm border-b-2 ${
                        activeTab === 'patients'
                          ? `border-[${websiteColorPalette.primary}] text-[${websiteColorPalette.primary}]`
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => setActiveTab('patients')}
                    >
                      Patients
                    </button>
                    <button
                      className={`py-3 sm:py-4 px-3 sm:px-6 font-medium text-xs sm:text-sm border-b-2 ${
                        activeTab === 'procedures'
                          ? `border-[${websiteColorPalette.primary}] text-[${websiteColorPalette.primary}]`
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => setActiveTab('procedures')}
                    >
                      Procedures
                    </button>
                    <button
                      className={`py-3 sm:py-4 px-3 sm:px-6 font-medium text-xs sm:text-sm border-b-2 ${
                        activeTab === 'performance'
                          ? `border-[${websiteColorPalette.primary}] text-[${websiteColorPalette.primary}]`
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => setActiveTab('performance')}
                    >
                      Performance
                    </button>
                    <button
                      className={`py-3 sm:py-4 px-3 sm:px-6 font-medium text-xs sm:text-sm border-b-2 ${
                        activeTab === 'trends'
                          ? `border-[${websiteColorPalette.primary}] text-[${websiteColorPalette.primary}]`
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => setActiveTab('trends')}
                    >
                      Trends
                    </button>
                  </nav>
                </div>
                <div className="p-4 sm:p-6">
                  {activeTab === 'overview' && (
                    <div>
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
                        <h2 className={`text-lg sm:text-xl font-bold text-[${websiteColorPalette.primary}] flex items-center`}>
                          <FaChartLine className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[${websiteColorPalette.primary}]`} />
                          University Overview
                        </h2>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center">
                            <FaCalendarAlt className={`h-5 w-5 text-[${websiteColorPalette.primary}] mr-3`} />
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500">Total Appointments</p>
                              <p className="text-lg sm:text-xl font-bold">{appointments.length}</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center">
                            <FaStar className={`h-5 w-5 text-[${websiteColorPalette.accent}] mr-3`} />
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500">Total Reviews</p>
                              <p className="text-lg sm:text-xl font-bold">{reviews.length}</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center">
                            <FaUserAlt className={`h-5 w-5 text-[${websiteColorPalette.secondary}] mr-3`} />
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500">Total Patients</p>
                              <p className="text-lg sm:text-xl font-bold">{patients.length}</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center">
                            <FaFileMedical className={`h-5 w-5 text-[${websiteColorPalette.primary}] mr-3`} />
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500">Total Procedures</p>
                              <p className="text-lg sm:text-xl font-bold">{treatmentSheets.length}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Appointments by Status</h3>
                          <div className="h-64">
                            <Pie data={appointmentsStatusChartData} options={pieChartOptions} />
                          </div>
                        </div>
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Reviews by Status</h3>
                          <div className="h-64">
                            <Pie data={reviewsStatusChartData} options={pieChartOptions} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'appointments' && (
                    <div>
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
                        <h2 className={`text-lg sm:text-xl font-bold text-[${websiteColorPalette.primary}] flex items-center`}>
                          <FaCalendarAlt className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[${websiteColorPalette.primary}]`} />
                          Appointment Analytics
                        </h2>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Appointments by Month</h3>
                          <div className="h-64">
                            <Bar data={appointmentsByMonthChartData} options={barChartOptions} />
                          </div>
                        </div>
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Appointments by Day</h3>
                          <div className="h-64">
                            <Bar data={appointmentsByDayChartData} options={barChartOptions} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'reviews' && (
                    <div>
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
                        <h2 className={`text-lg sm:text-xl font-bold text-[${websiteColorPalette.primary}] flex items-center`}>
                          <FaStar className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[${websiteColorPalette.primary}]`} />
                          Review Analytics
                        </h2>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Reviews by Procedure Type</h3>
                          <div className="h-64">
                            <Bar data={reviewsByProcedureTypeChartData} options={barChartOptions} />
                          </div>
                        </div>
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Reviews by Month</h3>
                          <div className="h-64">
                            <Bar data={reviewsByMonthChartData} options={barChartOptions} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'patients' && (
                    <div>
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
                        <h2 className={`text-lg sm:text-xl font-bold text-[${websiteColorPalette.primary}] flex items-center`}>
                          <FaUserAlt className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[${websiteColorPalette.primary}]`} />
                          Patient Analytics
                        </h2>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Patient Age Distribution</h3>
                          <div className="h-64">
                            <Bar data={patientsByAgeChartData} options={barChartOptions} />
                          </div>
                        </div>
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Patient Gender Distribution</h3>
                          <div className="h-64">
                            <Pie data={patientsByGenderChartData} options={pieChartOptions} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'procedures' && (
                    <div>
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
                        <h2 className={`text-lg sm:text-xl font-bold text-[${websiteColorPalette.primary}] flex items-center`}>
                          <FaFileMedical className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[${websiteColorPalette.primary}]`} />
                          Procedure Analytics
                        </h2>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Procedures by Type</h3>
                          <div className="h-64">
                            <Bar data={proceduresByTypeChartData} options={barChartOptions} />
                          </div>
                        </div>
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Procedures by Month</h3>
                          <div className="h-64">
                            <Bar data={proceduresByMonthChartData} options={barChartOptions} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'performance' && (
                    <div>
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
                        <h2 className={`text-lg sm:text-xl font-bold text-[${websiteColorPalette.primary}] flex items-center`}>
                          <FaUserGraduate className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[${websiteColorPalette.primary}]`} />
                          Performance Analytics
                        </h2>
                      </div>
                      
                      {/* Key Performance Indicators */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center">
                            <FaStar className={`h-5 w-5 text-[${websiteColorPalette.accent}] mr-3`} />
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500">Avg Procedure Quality</p>
                              <p className="text-lg sm:text-xl font-bold">{reviewsAnalytics.avgProcedureQuality}/5</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center">
                            <FaUserAlt className={`h-5 w-5 text-[${websiteColorPalette.secondary}] mr-3`} />
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500">Avg Patient Interaction</p>
                              <p className="text-lg sm:text-xl font-bold">{reviewsAnalytics.avgPatientInteraction}/5</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center">
                            <FaClipboardCheck className={`h-5 w-5 text-[${websiteColorPalette.primary}] mr-3`} />
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500">Review Acceptance Rate</p>
                              <p className="text-lg sm:text-xl font-bold">
                                {reviewsAnalytics.total > 0 ? 
                                  ((reviewsAnalytics.accepted / reviewsAnalytics.total) * 100).toFixed(1) : 0}%
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center">
                            <FaCalendarAlt className={`h-5 w-5 text-[${websiteColorPalette.orange}] mr-3`} />
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500">Appointment Completion Rate</p>
                              <p className="text-lg sm:text-xl font-bold">
                                {appointmentsAnalytics.total > 0 ? 
                                  ((appointmentsAnalytics.completed / appointmentsAnalytics.total) * 100).toFixed(1) : 0}%
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Student Performance</h3>
                          <div className="h-64">
                            <Bar data={studentPerformanceChartData} options={barChartOptions} />
                          </div>
                        </div>
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Clinic Efficiency</h3>
                          <div className="h-64">
                            <Line data={clinicEfficiencyChartData} options={lineChartOptions} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'trends' && (
                    <div>
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
                        <h2 className={`text-lg sm:text-xl font-bold text-[${websiteColorPalette.primary}] flex items-center`}>
                          <FaChartLine className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[${websiteColorPalette.primary}]`} />
                          Growth Trends & Analytics
                        </h2>
                      </div>
                      
                      {/* Trend Metrics */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500">Patient Growth Rate</p>
                              <p className="text-lg sm:text-xl font-bold text-green-600">
                                {getPatientTrends().patientCounts.length > 1 ? 
                                  ((getPatientTrends().patientCounts[getPatientTrends().patientCounts.length - 1] - 
                                    getPatientTrends().patientCounts[getPatientTrends().patientCounts.length - 2]) / 
                                   Math.max(getPatientTrends().patientCounts[getPatientTrends().patientCounts.length - 2], 1) * 100).toFixed(1)
                                  : 0}%
                              </p>
                            </div>
                            <FaChartLine className="h-8 w-8 text-green-500" />
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500">Avg Monthly Patients</p>
                              <p className="text-lg sm:text-xl font-bold text-blue-600">
                                {getPatientTrends().patientCounts.length > 0 ? 
                                  (getPatientTrends().patientCounts.reduce((a, b) => a + b, 0) / getPatientTrends().patientCounts.length).toFixed(1) : 0}
                              </p>
                            </div>
                            <FaUserAlt className="h-8 w-8 text-blue-500" />
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500">Total New Patients</p>
                              <p className="text-lg sm:text-xl font-bold text-purple-600">
                                {getPatientTrends().patientCounts.reduce((a, b) => a + b, 0)}
                              </p>
                            </div>
                            <FaUsers className="h-8 w-8 text-purple-500" />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Patient Growth Trends</h3>
                          <div className="h-64">
                            <Line data={patientGrowthTrendsData} options={lineChartOptions} />
                          </div>
                        </div>
                        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
                          <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4">Review Quality Trends</h3>
                          <div className="h-64">
                            <Line data={reviewQualityTrendsData} options={lineChartOptions} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Analytics;